import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatCurrency, calculateOrderProfit } from '../../utils/helpers';

const getWeekKey = (d) => {
    const date = new Date(d);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    const week1 = new Date(date.getFullYear(), 0, 4);
    const weekNumber = 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
    return `T${weekNumber}/${date.getFullYear()}`;
}

export const StatisticsChart = ({ data, groupBy, setGroupBy }) => {
    const initialMetrics = {
        'Tổng Doanh số': true,
        'Tiền hàng': false,
        'Chi phí': false,
        'Lợi nhuận gộp': false,
        'Lợi nhuận ròng': true,
        'Doanh số Trực tiếp': false,
        'Doanh số Website': false,
        'Doanh số Oreka': false,
        'Doanh số Lazada': false,
    };

    const [visibleMetrics, setVisibleMetrics] = useState(initialMetrics);

    const handleMetricChange = (metric) => {
        setVisibleMetrics(prev => ({ ...prev, [metric]: !prev[metric] }));
    };

    const chartData = useMemo(() => {
        if (!data || data.length === 0) return [];
        
        const groupedData = data.reduce((acc, item) => {
            const date = new Date(item.date);
            const [year, month, day] = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')];
            let key;
            let sortKey;
            
            switch(groupBy) {
                case 'Tuần':
                    key = getWeekKey(date);
                    sortKey = `${year}-${month}-${day}`;
                    break;
                case 'Tháng':
                    key = `${month}/${year}`;
                    sortKey = `${year}-${month}`;
                    break;
                case 'Năm':
                    key = `${year}`;
                    sortKey = `${year}`;
                    break;
                case 'Ngày':
                default:
                    key = `${day}/${month}/${year}`;
                    sortKey = `${year}-${month}-${day}`;
                    break;
            }

            if (!acc[key]) {
                acc[key] = {
                    ...Object.keys(initialMetrics).reduce((obj, k) => ({...obj, [k]: 0}), {}),
                    name: key,
                    sortKey: sortKey
                };
            }

            const { profit: orderProfit } = calculateOrderProfit(item);
            const orderRevenue = (item.items.reduce((s, i) => s + (i.price * i.quantity), 0)) + (item.shippingFee || 0) - (item.promotion || 0);

            acc[key]['Tổng Doanh số'] += orderRevenue;
            acc[key]['Tiền hàng'] += item.items.reduce((s, i) => s + (i.cost * i.quantity), 0);
            acc[key]['Chi phí'] += (item.realShippingFee || 0) + (item.platformFee || 0);
            if(orderProfit !== null) {
                acc[key]['Lợi nhuận ròng'] += orderProfit;
            }
            
            const sourceKey = `Doanh số ${item.source}`;
            if(acc[key].hasOwnProperty(sourceKey)) {
                acc[key][sourceKey] += orderRevenue;
            }

            return acc;
        }, {});
        
        return Object.values(groupedData)
            .map((values) => {
                values['Lợi nhuận gộp'] = values['Tổng Doanh số'] - values['Tiền hàng'] - values['Chi phí'];
                return values;
            })
            .sort((a,b) => a.sortKey.localeCompare(b.sortKey));

    }, [data, groupBy]);

    const colors = {
        'Tổng Doanh số': '#3b82f6',
        'Lợi nhuận ròng': '#22c55e',
        'Lợi nhuận gộp': '#10b981',
        'Tiền hàng': '#f97316',
        'Chi phí': '#ef4444',
        'Doanh số Trực tiếp': '#8b5cf6',
        'Doanh số Website': '#6366f1',
        'Doanh số Oreka': '#a855f7',
        'Doanh số Lazada': '#ec4899',
    };
    
    if (chartData.length === 0) {
        return <div className="text-center p-10 text-gray-500">Không có dữ liệu để hiển thị.</div>;
    }

    return (
        <div className="bg-white p-5 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg text-gray-800">Biểu đồ thống kê</h3>
                <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="p-2 border rounded-md bg-white shadow-sm text-sm">
                    <option value="Ngày">Theo Ngày</option>
                    <option value="Tuần">Theo Tuần</option>
                    <option value="Tháng">Theo Tháng</option>
                    <option value="Năm">Theo Năm</option>
                </select>
            </div>
            <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} interval={'preserveStartEnd'} />
                        <YAxis tickFormatter={(value) => new Intl.NumberFormat('vi-VN').format(value)} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        {Object.keys(visibleMetrics).map(metric => 
                            visibleMetrics[metric] && <Bar key={metric} dataKey={metric} fill={colors[metric]} />
                        )}
                    </BarChart>
                </ResponsiveContainer>
            </div>
             <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 pt-4 border-t">
                {Object.keys(initialMetrics).map(metric => (
                    <label key={metric} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                            type="checkbox"
                            checked={visibleMetrics[metric]}
                            onChange={() => handleMetricChange(metric)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {metric}
                    </label>
                ))}
            </div>
        </div>
    );
};